import { CloseOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { defaultCategory } from 'config/defaultCategory';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllCategoryFields, selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import EditableDeviceField from 'zComponents/molecules/EditableDeviceField';
import { Button, Col, Flex, Form, Modal, Row, Select, Space, Tooltip, Typography } from 'zui/Antd';
const { Text, Title } = Typography;

const AdditionalFieldsModal = ({
	children,
	visible: visibleProp,
	setVisible: setVisibleProp,
	open,
	setOpen,
	deviceID
}) => {
	// Hooks
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const {
		token: {
			paddingLG,
			paddingSM,
			paddingXL,
			fontSizeSM,
			colorTextDescription,
			fontWeightStrong,
			marginMD,
			fontSize,
			paddingMD,
			paddingXXS,
			paddingXS
		}
	} = theme.useToken();

	// Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const [fields, setFields] = useState([{ fieldID: undefined, value: undefined }]);
	const [selectedFields, setSelectedFields] = useState({});

	// Redux state
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const allCategoryFields = useSelector(selectAllCategoryFields(device?.categoryID));
	const { createDeviceFieldLoading, updateDeviceFieldLoading } = useSelector((state) => state.Devices);

	const visible = visibleProp ?? open ?? internalVisible;
	const setVisible = setVisibleProp ?? setOpen ?? setInternalVisible;

	const isLoading = createDeviceFieldLoading || updateDeviceFieldLoading;

	const categoryConfig = useMemo(() => {
		return defaultCategory[device?.defaultCategoryID] || null;
	}, [device?.defaultCategoryID]);

	const isHealthAndSafety = useMemo(() => {
		return device?.defaultCategoryID === 1;
	}, [device?.defaultCategoryID]);

	// Check if field can be edited based on category configuration
	const canEditField = useCallback(
		(fieldTitle) => {
			if (!categoryConfig || isHealthAndSafety) return true;
			const editable = categoryConfig?.editableCustomFields;
			if (!Array.isArray(editable)) return false;
			if (editable.length === 0) return false;
			return editable.includes(fieldTitle);
		},
		[categoryConfig, isHealthAndSafety]
	);

	const availableFieldsForEditing = useMemo(() => {
		if (!allCategoryFields) return [];

		return allCategoryFields.filter((categoryField) => {
			// Check if this field already exists on the device
			const existingField = device?.fields?.find((f) => f.fieldID === categoryField.fieldID);

			if (!existingField) return true;

			if (!existingField.value || existingField.value === '') return true;

			return canEditField(categoryField.title);
		});
	}, [allCategoryFields, device?.fields, canEditField]);

	// Check if there are valid changes to submit
	const hasValidChanges = useMemo(() => {
		return fields.some((field) => field.fieldID && field.value !== undefined && field.value !== '');
	}, [fields]);

	// Add new empty field to state
	const handleAdd = () => {
		setFields([...fields, { fieldID: undefined, value: undefined }]);
	};

	// Remove field from state
	const handleRemove = (index) => {
		if (fields.length === 1) {
			setFields([{ fieldID: undefined, value: undefined }]);
		} else {
			const updated = [...fields];
			updated.splice(index, 1);
			setFields(updated);
		}
	};

	// Update field state and track selected fields to filter options
	const handleFieldChange = (index, key, value) => {
		const updated = [...fields];

		if (key === 'fieldID') {
			const existingField = device?.fields?.find((f) => f.fieldID === value);
			const newValue = existingField ? existingField.value : undefined;

			updated[index] = {
				fieldID: value,
				value: newValue
			};
			setSelectedFields((prev) => ({ ...prev, [index]: value }));
		} else {
			updated[index][key] = value;
		}

		setFields(updated);

		form.setFieldsValue({ fields: updated });
	};

	// Reset state and close modal
	const handleCancel = () => {
		setFields([{ fieldID: undefined, value: undefined }]);
		setSelectedFields({});
		setVisible(false);
		form.resetFields();
	};

	const handleUpdate = () => {
		const validFields = fields.filter((f) => f.fieldID && f.value != null && f.value !== '');
		if (validFields.length) {
			// Separate new fields from existing fields
			const newFields = [];
			const updatedFields = [];

			validFields.forEach((field) => {
				const existingField = device?.fields?.find((f) => f.fieldID === field.fieldID);
				if (existingField) {
					updatedFields.push(field);
				} else {
					newFields.push(field);
				}
			});

			if (newFields.length > 0) {
				dispatch(devicesActions.createDeviceField({ deviceID, data: { fields: newFields } }));
			}
			if (updatedFields.length > 0) {
				dispatch(devicesActions.updateDeviceField({ deviceID, data: { fields: updatedFields } }));
			}
		}

		handleCancel();
	};

	// Determine if more fields can be added
	const canAddMoreFields = fields.length < availableFieldsForEditing.length;

	return (
		<>
			<Tooltip title='Additional Fields' placement='top'>
				{children ? (
					children
				) : (
					<Button icon={<UserOutlined />} onClick={() => setVisible(true)}>
						Add Fields
					</Button>
				)}
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={661}
				footer={[
					<div key='modal-footer'>
						<Flex align='end' justify='flex-end' gap={paddingSM}>
							<Button onClick={handleCancel} type='default' disabled={isLoading}>
								Cancel
							</Button>
							<Button
								onClick={handleUpdate}
								type='primary'
								disabled={!hasValidChanges || isLoading}
								loading={isLoading}
							>
								Update
							</Button>
						</Flex>
					</div>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` }, body: { height: 430 } }}
			>
				<Row gutter={[0, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Edit Custom Fields
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Customize additional fields to capture specific information relevant to your device. {' '}
						</Text>
					</Col>
					<Space direction='vertical' size={paddingXS} style={{ width: '100%' }}>
						<Text style={{ fontWeight: fontWeightStrong }}>Category Custom Fields</Text>

						<Form
							form={form}
							layout='vertical'
							style={{
								height: 300,
								overflowY: 'auto',
								overflowX: 'hidden',
								padding: 2
							}}
						>
							{fields.map((fieldItem, index) => {
								const selectedFieldID = selectedFields[index] || fieldItem.fieldID;
								const categoryField = availableFieldsForEditing.find(
									(f) => f.fieldID === selectedFieldID
								);

								const filteredOptions = availableFieldsForEditing
									.filter((f) => !fields.some((item, i) => i !== index && item.fieldID === f.fieldID))
									.map((f) => ({ label: f.title, value: f.fieldID }));

								return (
									<Row
										key={index}
										gutter={[paddingMD, paddingMD]}
										style={{ marginBottom: marginMD }}
										align='middle'
									>
										<Col xs={24} md={12}>
											<Form.Item
												name={['fields', index, 'fieldID']}
												rules={[{ required: true, message: 'Please select a field' }]}
												style={{ marginBottom: 0 }}
											>
												<Select
													placeholder='Select Field'
													options={filteredOptions}
													value={fieldItem.fieldID}
													onChange={(value) => handleFieldChange(index, 'fieldID', value)}
												/>
											</Form.Item>
										</Col>

										<Col style={{ position: 'relative' }} xs={24} md={12}>
											<Flex justify='space-between' align='center'>
												<Form.Item
													name={['fields', index, 'value']}
													rules={[{ required: true, message: 'Please enter a value' }]}
													style={{ marginBottom: 0, flex: 1 }}
												>
													<EditableDeviceField
														key={fieldItem.fieldID || 'new-field'}
														fieldTypeID={categoryField?.fieldTypeID}
														value={fieldItem.value}
														onChange={(value) => handleFieldChange(index, 'value', value)}
														onChangeInput={(value) =>
															handleFieldChange(index, 'value', value)
														}
														disabled={isLoading}
													/>
												</Form.Item>
												<Tooltip title='Remove Field'>
													<Button
														type='text'
														style={{ marginLeft: paddingXXS }}
														icon={
															<CloseOutlined
																style={{
																	fontSize: fontSize,
																	color: colorTextDescription
																}}
															/>
														}
														onClick={() => handleRemove(index)}
														disabled={isLoading}
													/>
												</Tooltip>
											</Flex>
										</Col>
									</Row>
								);
							})}

							{canAddMoreFields && (
								<Form.Item>
									<Button type='dashed' onClick={handleAdd} block icon={<PlusOutlined />}>
										Add Field
									</Button>
								</Form.Item>
							)}
						</Form>
					</Space>
				</Row>
			</Modal>
		</>
	);
};

export default AdditionalFieldsModal;
